import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { 
  Form, 
  FormControl, 
  FormDescription, 
  FormField, 
  FormItem, 
  FormLabel, 
  FormMessage 
} from "@/components/ui/form";
import { toast } from "@/hooks/use-toast";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { uploadResourceFile, createInsightResource, updateInsightResource } from "@/services/insightResourceService";
import type { InsightResource } from "@/services/insightResourceService";
import { supabase } from "@/integrations/supabase/client";

const resourceTypeOptions = [
  { value: "whitepaper", label: "White Paper" },
  { value: "checklist", label: "Checklist" },
  { value: "guide", label: "Guide" },
  { value: "template", label: "Template" },
  { value: "report", label: "Report" },
];

const resourceFormSchema = z.object({
  title: z.string().min(3, "Title must be at least 3 characters"),
  description: z.string().optional(),
  resource_type: z.string().min(1, "Resource type is required"),
  is_gated: z.boolean().default(true),
});

type ResourceFormValues = z.infer<typeof resourceFormSchema>;

interface InsightResourceFormProps {
  resource?: InsightResource;
  onSuccess: () => void;
  onCancel: () => void;
}

const InsightResourceForm: React.FC<InsightResourceFormProps> = ({
  resource,
  onSuccess,
  onCancel,
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<ResourceFormValues>({
    resolver: zodResolver(resourceFormSchema),
    defaultValues: {
      title: resource?.title || "",
      description: resource?.description || "",
      resource_type: resource?.resource_type || "whitepaper",
      is_gated: resource ? resource.is_gated : true,
    },
  });

  const handleSubmit = async (values: ResourceFormValues) => {
    if (!file && !resource) {
      toast({
        title: "File required",
        description: "Please upload a resource file",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsSubmitting(true);
      
      let filePath = resource?.file_path || "";
      let imageUrl = resource?.image_url || null;
      
      // Upload new file if provided
      if (file) {
        filePath = await uploadResourceFile(file, values.resource_type);
      }
      
      // Upload new image if provided
      if (imageFile) {
        const imagePath = await uploadResourceFile(imageFile, "thumbnails");
        const { data } = supabase.storage.from("resources").getPublicUrl(imagePath);
        imageUrl = data.publicUrl;
      }

      // Create or update resource
      if (resource) {
        await updateInsightResource(resource.id, {
          title: values.title,
          description: values.description || null,
          resource_type: values.resource_type,
          is_gated: values.is_gated,
          ...(filePath ? { file_path: filePath } : {}),
          ...(imageUrl ? { image_url: imageUrl } : {}),
        });
        
        toast({
          title: "Resource updated",
          description: "The resource has been updated successfully",
        });
      } else {
        await createInsightResource({
          title: values.title,
          description: values.description || null,
          resource_type: values.resource_type,
          is_gated: values.is_gated,
          file_path: filePath,
          image_url: imageUrl,
        });
        
        toast({
          title: "Resource created",
          description: "The resource has been created successfully",
        });
      }
      
      onSuccess();
    } catch (error) {
      console.error("Error saving resource:", error);
      toast({
        title: "Error",
        description: "There was a problem saving the resource",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
        <FormField
          control={form.control}
          name="title"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Title*</FormLabel>
              <FormControl>
                <Input placeholder="Resource Title" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Description</FormLabel>
              <FormControl>
                <Textarea 
                  placeholder="Provide a description of this resource" 
                  {...field} 
                  value={field.value || ""}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="resource_type"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Resource Type*</FormLabel>
              <FormControl>
                <select
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2"
                  {...field}
                >
                  {resourceTypeOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="is_gated"
          render={({ field }) => (
            <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
              <div className="space-y-0.5">
                <FormLabel className="text-base">Require Form Submission</FormLabel>
                <FormDescription>
                  When enabled, users must provide their information before downloading
                </FormDescription>
              </div>
              <FormControl>
                <Switch
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
            </FormItem>
          )}
        />
        
        <div className="space-y-4">
          <div>
            <Label>Resource File*</Label>
            <Input
              type="file"
              onChange={(e) => {
                if (e.target.files && e.target.files[0]) {
                  setFile(e.target.files[0]);
                }
              }}
              className="mt-1"
            />
            {resource?.file_path && !file && (
              <p className="text-sm text-muted-foreground mt-1">
                Current file: {resource.file_path.split('/').pop()}
              </p>
            )}
          </div>
          
          <div>
            <Label>Thumbnail Image</Label>
            <Input
              type="file"
              accept="image/*"
              onChange={(e) => {
                if (e.target.files && e.target.files[0]) {
                  setImageFile(e.target.files[0]);
                }
              }}
              className="mt-1"
            />
            {resource?.image_url && !imageFile && (
              <div className="mt-2">
                <p className="text-sm text-muted-foreground mb-1">Current thumbnail:</p>
                <img 
                  src={resource.image_url} 
                  alt="Current thumbnail" 
                  className="h-16 w-auto object-cover rounded border"
                />
              </div>
            )}
          </div>
        </div>
        
        <div className="flex justify-end space-x-2">
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? "Saving..." : resource ? "Update Resource" : "Create Resource"}
          </Button>
        </div>
      </form>
    </Form>
  );
};

export default InsightResourceForm;
